import React from 'react';

import useDesktopObservation from '@/hooks/useDesktopObservation';
import { isDesktopApp } from '@/api/desktopSync';
import { orderDataProgressPresentation } from '@/utils/orderDataProgress';
import useMediaReadinessStatus from '@/hooks/useMediaReadinessStatus';
import { mediaReadinessNotice } from '@/utils/mediaReadinessPresentation';

export default function DesktopOfflineReadinessNotice() {
  const desktop = window.lvbDesktop;
  const isUsingLocal = Boolean(
    desktop?.backendUrl
    && desktop?.localBackendUrl
    && desktop.backendUrl === desktop.localBackendUrl,
  );
  const { status, phase } = useDesktopObservation('getOrderDataStatus', 'onOrderDataStatusChanged');
  const { status: mediaStatus } = useMediaReadinessStatus();

  if (!isDesktopApp()) return null;
  const acquisitionFailed = phase === 'unavailable' || (status?.receiptAccepted === false
    && status?.reason && status.reason !== 'offline_order_data_not_enabled');
  if (isUsingLocal && acquisitionFailed) return (
    <div className="px-3 py-2 bg-amber-100 text-amber-950 text-xs" data-testid="desktop-offline-status-unavailable">
      Offline order status could not be confirmed. This does not establish that saved data is missing.
    </div>
  );
  const progress = orderDataProgressPresentation(status?.snapshotProgress);
  if (!isUsingLocal && progress?.operation === 'bootstrap') {
    return (
      <div
        className="px-3 py-2 bg-amber-100 text-amber-950 text-xs font-medium flex-shrink-0"
        data-testid="desktop-offline-data-install-progress"
      >
        Installing offline order data: {progress.label} (step {progress.step} of {progress.totalSteps}).
      </div>
    );
  }
  const notEnabled = status?.operationState === 'not_enabled'
    || status?.operationReason === 'offline_order_data_not_enabled';
  if (!isUsingLocal && notEnabled) {
    return (
      <div
        className="px-3 py-2 bg-amber-100 text-amber-950 text-xs font-medium flex-shrink-0"
        data-testid="desktop-offline-data-not-enabled-notice"
      >
        Offline order data is not enabled. Initial order history is available for installation to enable offline mode.
      </div>
    );
  }
  if (isUsingLocal && status?.receiptAccepted === false) {
    return (
      <div
        className="px-3 py-2 bg-red-800 text-white text-xs font-medium flex-shrink-0"
        data-testid="desktop-offline-readiness-notice"
      >
        This computer has no accepted offline order baseline. Return online to finish preparing offline data; existing device-owned work has not been erased.
      </div>
    );
  }
  const mediaNotice = mediaReadinessNotice(mediaStatus);
  if (!mediaNotice) return null;
  return (
    <div
      className={`px-3 py-2 text-xs font-medium flex-shrink-0 ${
        mediaNotice.tone === 'red' ? 'bg-red-800 text-white' : 'bg-amber-100 text-amber-950'
      }`}
      data-testid="desktop-offline-media-readiness-notice"
    >
      {mediaNotice.text}
    </div>
  );
}
